<template>
  <div :style="{ width: `${size}px`, height: `${size}px`, position: 'relative' }">
    <svg
      :viewBox="`0 0 ${size} ${size}`"
      :width="size"
      :height="size"
      :style="{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none', display: 'block' }"
      @pointerdown="onDown"
      @pointermove="onMove"
      @pointerup="onUp"
      @pointerleave="onUp"
    >
      <defs>
        <radialGradient id="mm-globe-fill" cx="38%" cy="32%" r="80%">
          <stop offset="0%" stop-color="#0d1526" />
          <stop offset="70%" stop-color="#080e1c" />
          <stop offset="100%" stop-color="#040810" />
        </radialGradient>
      </defs>

      <!-- atmosphere glow -->
      <circle :cx="cx" :cy="cy" :r="R + 8" fill="none" stroke="rgb(79,107,255)" stroke-opacity="0.18" stroke-width="2" />
      <!-- ocean sphere -->
      <path :d="spherePath" fill="url(#mm-globe-fill)" stroke="rgb(79,107,255)" stroke-opacity="0.25" stroke-width="0.8" />

      <!-- static landmasses -->
      <path
        v-if="shapes && shapes._static"
        :d="getPath(shapes._static)"
        fill="#0d1a30"
        fill-opacity="0.9"
        stroke="rgb(79,107,255)"
        stroke-width="0.7"
        stroke-opacity="0.5"
      />

      <!-- interactive region continents -->
      <template v-if="shapes">
        <template v-for="r in enrichedRegions" :key="r.slug">
          <path
            v-if="shapes[r.slug]"
            :d="getPath(shapes[r.slug])"
            :fill="isSelected(r) ? 'rgb(79,107,255)' : '#0d1a30'"
            :fill-opacity="isSelected(r) ? 0.45 : 0.9"
            :stroke="isSelected(r) ? 'rgb(120,145,255)' : 'rgb(79,107,255)'"
            :stroke-opacity="isSelected(r) ? 0.9 : 0.5"
            :stroke-width="isSelected(r) ? 1.0 : 0.7"
            :style="`cursor: ${disabled ? 'default' : 'pointer'}; transition: fill 150ms, fill-opacity 150ms`"
            @click="onRegionClick(r)"
          >
            <title>{{ r.name }}</title>
          </path>
        </template>
      </template>

      <!-- graticule -->
      <path :d="graticulePath" fill="none" stroke="rgb(79,107,255)" stroke-width="0.5" stroke-opacity="0.2" />

      <!-- rim -->
      <circle :cx="cx" :cy="cy" :r="R" fill="none" stroke="rgb(79,107,255)" stroke-opacity="0.3" stroke-width="1" />

      <!-- city pin -->
      <g v-if="pin" pointer-events="none">
        <circle :cx="pin[0]" :cy="pin[1]" r="11" fill="none" stroke="rgb(99,102,241)" stroke-width="2" class="globe-pulse" />
        <circle :cx="pin[0]" :cy="pin[1]" r="4.5" fill="rgb(99,102,241)" stroke="#fff" stroke-width="1.5" />
      </g>

      <!-- loading shimmer -->
      <text
        v-if="!shapes"
        :x="cx"
        :y="cy"
        text-anchor="middle"
        dominant-baseline="middle"
        fill="#6b7280"
        font-size="11"
      >{{ $t('play.matchmaking.globe_loading') }}</text>
    </svg>
  </div>
</template>

<script setup>
import {
  ref, computed, watch, onMounted, onUnmounted,
} from 'vue';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import worldData from 'world-atlas/countries-110m.json';

const STATIC_ANCHORS = [
  [12, 20], [22, 2], [25, -24], [-5, 8],
  [0, -82],
];

const EXTRA_ANCHORS = [
  { slug: 'na', c: [-100, 55] }, { slug: 'na', c: [-112, 39] }, { slug: 'na', c: [-40, 72] },
  { slug: 'europe', c: [20, 48] }, { slug: 'europe', c: [25, 58] },
  { slug: 'pacific', c: [145, -25] }, { slug: 'pacific', c: [120, 25] }, { slug: 'pacific', c: [90, 20] },
];

const REGION_GEO = {
  europe: { lat: 50, lon: 8, city: 'Frankfurt' },
  na: { lat: 41, lon: -87, city: 'Chicago' },
  pacific: { lat: -33, lon: 151, city: 'Sydney' },
};

const props = defineProps({
  regions: { type: Array, default: () => [] },
  modelValue: { type: Object, default: null },
  size: { type: Number, default: 300 },
  // Locked while the party is queued, and for everyone who is not the leader.
  // Only region selection locks — the globe stays draggable so a member can
  // still spin it to look at the queued region.
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

const R = computed(() => props.size / 2 - 14);
const cx = computed(() => props.size / 2);
const cy = computed(() => props.size / 2);

const enrichedRegions = computed(() => props.regions.map((r) => ({
  ...r,
  ...(REGION_GEO[r.slug] ?? { lat: 0, lon: 0, city: '' }),
})));

const selectedGeo = computed(() => enrichedRegions.value.find((r) => r.slug === props.modelValue?.slug)
  ?? enrichedRegions.value[0]);

const rot = ref([-(selectedGeo.value?.lon ?? 0), -(selectedGeo.value?.lat ?? 0)]);
const shapes = ref(null);
const isDragging = ref(false);

const drag = {
  on: false, x: 0, y: 0, moved: false, captured: false,
};
let animId = null;

// graticule is constant
const graticule = d3.geoGraticule().step([30, 30])();

const projection = computed(() => d3.geoOrthographic()
  .scale(R.value)
  .translate([cx.value, cy.value])
  .rotate([rot.value[0], rot.value[1], 0])
  .clipAngle(90));

const pathFn = computed(() => d3.geoPath(projection.value));

const spherePath = computed(() => pathFn.value({ type: 'Sphere' }) ?? '');
const graticulePath = computed(() => pathFn.value(graticule) ?? '');

function getPath(geo) {
  return pathFn.value(geo) ?? '';
}

const pin = computed(() => {
  if (!selectedGeo.value) return null;
  const { lon, lat } = selectedGeo.value;
  const rotCenter = [-rot.value[0], -rot.value[1]];
  if (d3.geoDistance([lon, lat], rotCenter) > Math.PI / 2) return null;
  return projection.value([lon, lat]);
});

function isSelected(r) {
  return r.slug === props.modelValue?.slug;
}

// pointer drag
function onDown(e) {
  cancelAnimationFrame(animId);
  drag.on = true;
  drag.x = e.clientX;
  drag.y = e.clientY;
  drag.moved = false;
  drag.captured = false;
  isDragging.value = true;
}

function onMove(e) {
  if (!drag.on) return;
  const dx = e.clientX - drag.x;
  const dy = e.clientY - drag.y;
  if (Math.abs(dx) + Math.abs(dy) > 3) {
    drag.moved = true;
    if (!drag.captured) {
      try { e.currentTarget.setPointerCapture(e.pointerId); drag.captured = true; } catch (_) {}
    }
  }
  drag.x = e.clientX;
  drag.y = e.clientY;
  rot.value = [rot.value[0] + dx * 0.4, Math.max(-90, Math.min(90, rot.value[1] - dy * 0.4))];
}

function onUp(e) {
  if (!drag.on) return;
  drag.on = false;
  isDragging.value = false;
  if (drag.captured) {
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
    drag.captured = false;
  }
}

function onRegionClick(r) {
  if (drag.moved) { drag.moved = false; return; }
  if (props.disabled) return;
  const region = props.regions.find((br) => br.slug === r.slug);
  if (region) emit('update:modelValue', region);
}

// spin to selected region when modelValue changes
watch(() => props.modelValue?.slug, () => {
  const geo = selectedGeo.value;
  if (!geo) return;
  const start = [...rot.value];
  const target = [-geo.lon, -geo.lat];
  const dl = (((target[0] - start[0]) % 360) + 540) % 360 - 180;
  const dp = target[1] - start[1];
  const dur = 820;
  const t0 = performance.now();
  cancelAnimationFrame(animId);
  const tick = (now) => {
    const p = Math.min(1, (now - t0) / dur);
    const e = 1 - (1 - p) ** 3;
    rot.value = [start[0] + dl * e, start[1] + dp * e];
    if (p < 1) animId = requestAnimationFrame(tick);
  };
  animId = requestAnimationFrame(tick);
});

// build shapes from world atlas — called once regions are available
function buildShapes(regions) {
  if (!regions.length) return;
  try {
    const topo = worldData;
    const geoms = topo.objects.countries.geometries;

    const knownSlugs = new Set(regions.map((r) => r.slug));

    const anchors = regions
      .map((r) => ({ slug: r.slug, c: [r.lon, r.lat] }))
      .concat(EXTRA_ANCHORS.filter((a) => knownSlugs.has(a.slug)))
      .concat(STATIC_ANCHORS.map((c) => ({ slug: null, c })));

    const buckets = { _static: [] };
    regions.forEach((r) => (buckets[r.slug] = []));

    for (const g of geoms) {
      const f = topojson.feature(topo, g);
      const ctr = d3.geoCentroid(f);
      let best = null; let
        bestD = Infinity;
      for (const a of anchors) {
        const dist = d3.geoDistance(ctr, a.c);
        if (dist < bestD) { bestD = dist; best = a; }
      }
      const slug = best?.slug ?? '_static';
      (buckets[slug] ?? buckets._static).push(g);
    }

    const out = {};
    regions.forEach((r) => {
      if (buckets[r.slug]?.length) out[r.slug] = topojson.merge(topo, buckets[r.slug]);
    });
    if (buckets._static.length) out._static = topojson.merge(topo, buckets._static);
    shapes.value = out;
  } catch (err) {
    console.error('WorldGlobe: failed to build shapes', err);
  }
}

// rebuild whenever regions prop arrives (they load async from the backend)
watch(() => props.regions, (regions) => buildShapes(enrichedRegions.value), { immediate: true });

onUnmounted(() => cancelAnimationFrame(animId));
</script>

<style scoped>
.globe-pulse {
  animation: globe-ring 1.8s ease-out infinite;
}
@keyframes globe-ring {
  0%   { r: 6;  opacity: 0.85; }
  100% { r: 17; opacity: 0; }
}
</style>
